import React, { useState } from "react";
import { colors, fonts, LEFT_NAV_WIDTH, UNIT } from "../styles";
import { Image, Pressable, View } from "react-native";
import { Text } from "react-native-elements";
import { Persona } from "../types";
import { useNavigation } from "@react-navigation/native";
import { getFocusedRouteNameFromRoute, useRoute } from "@react-navigation/core";
import { StackNavigationProp } from "@react-navigation/stack";
import { RootStackParamList, defaultRoutes } from "../../App";
import { footerElements } from "../footer";
import { TEST_ID_PROPERTY_MANAGER_NAV_DASHBOARD } from "../constants";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { leftNavOpenState } from "../state/leftNav";
import { mobileModeState } from "../state/mobileMode";
import { TextLink } from "./TextLink";
import { useDemoMode } from "../useDemoMode";

const entryRightPadding = UNIT * 2;

const styles = {
  leftNav: {
    backgroundColor: colors.neutral100,
    paddingTop: 32,
    width: LEFT_NAV_WIDTH,
  },
  navText: {
    fontFamily: fonts.header,
    lineHeight: 24,
    textAlign: "right",
    color: colors.neutral600,
    fontSize: 18,
  },
  navEntry: {
    top: 0,
    paddingTop: 5,
    height: UNIT * 4,
    paddingBottom: 4,
    paddingRight: entryRightPadding,
  },
  navSelected: {
    color: colors.primary,
    // : "white",
  },
  navHovered: {
    backgroundColor: colors.primary300,
    color: "white",
  },
  navDisabled: {
    color: "grey",
  },
} as const;

type LinkProps = {
  label: string;
  // The currently rendered route
  activeRouteName: string | undefined;
  routeName?: keyof RootStackParamList;
  testID?: string;
};

const Link = ({ label, routeName, activeRouteName, testID }: LinkProps) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const setLeftNavOpen = useSetRecoilState(leftNavOpenState);
  const [hovered, setHovered] = useState<boolean>(false);

  const hoverEvents = {
    onMouseOver: () => {
      setHovered(true);
    },
    onMouseOut: () => {
      setHovered(false);
    },
  };

  const linkStyle =
    activeRouteName === routeName
      ? styles.navSelected
      : !routeName
      ? styles.navDisabled
      : hovered
      ? styles.navHovered
      : {};

  const onPress = routeName
    ? () => {
        setLeftNavOpen(false);
        navigation.navigate(routeName);
      }
    : undefined;

  return (
    <Text
      style={{ ...styles.navEntry, ...styles.navText, ...linkStyle }}
      onPress={onPress}
      testID={testID}
      {...hoverEvents}
    >
      {label}
    </Text>
  );
};

function personaToItems(persona: Persona, activeRouteName: string | undefined) {
  switch (persona) {
    case Persona.PropertyManager: {
      return (
        <>
          <Link
            label="Company"
            routeName="PropertyManagerCompany"
            activeRouteName={activeRouteName}
          />
          <Link
            label="Dashboard"
            routeName="PropertyManagerHome"
            activeRouteName={activeRouteName}
            testID={TEST_ID_PROPERTY_MANAGER_NAV_DASHBOARD}
          />
          <Link
            label="Properties"
            routeName="PropertyManagerProperties"
            activeRouteName={activeRouteName}
          />
          <Link
            label="Providers"
            routeName="PropertyManagerProviders"
            activeRouteName={activeRouteName}
          />
          <Link
            label="Market Trends"
            routeName="PropertyManagerTrends"
            activeRouteName={activeRouteName}
          />
          <Link
            label="Proposals"
            routeName="PropertyManagerProposals"
            activeRouteName={activeRouteName}
          />
        </>
      );
    }
    case Persona.ServiceProvider: {
      return (
        <>
          <Link
            label="RFPs"
            routeName="SubcontractorHome"
            activeRouteName={activeRouteName}
          />
        </>
      );
    }
    case Persona.Metrologist: {
      return (
        <>
          <Link
            label="Need Measurement"
            routeName="MetrologistHome"
            activeRouteName={activeRouteName}
          />
        </>
      );
    }
  }
}

function profileRoute(persona: Persona) {
  switch (persona) {
    case Persona.ServiceProvider:
      return "SubcontractorProfile";
    case Persona.PropertyManager:
      return "PropertyManagerProfile";
    case Persona.Metrologist:
      return "MetrologistProfile";
  }
}

type LeftNavProps = {
  persona: Persona;
};

const useFocusedRouteName = () => {
  const route = useRoute();
  const focusedRouteName = getFocusedRouteNameFromRoute(route) || route.name;
  if (!focusedRouteName) return undefined;
  if (focusedRouteName in defaultRoutes)
    return (defaultRoutes as any)[focusedRouteName];
  return focusedRouteName;
};

export default function LeftNav(props: LeftNavProps) {
  const focusedRouteName = useFocusedRouteName();
  const mobileMode = useRecoilValue(mobileModeState);

  const demoMode = useDemoMode();
  const navigation = useNavigation();
  const seeDataAgreement = () =>
    navigation.navigate("Open", { screen: "DataAgreement" });

  return (
    <View style={styles.leftNav}>
      {personaToItems(props.persona, focusedRouteName)}
      <View style={styles.navEntry} />
      <Link
        label="Profile"
        routeName={profileRoute(props.persona)}
        activeRouteName={focusedRouteName}
      />

      {demoMode && (
        <Pressable onPress={seeDataAgreement}>
          <View
            style={{
              borderRadius: UNIT / 2,
              backgroundColor: colors.neutral800,
              padding: UNIT,
              margin: UNIT * 2,
              alignItems: "center",
            }}
          >
            <Image
              source={require("../../assets/img/shield.png")}
              style={{ width: UNIT * 6, height: UNIT * 6, alignSelf: "center" }}
            />
            <Text style={{ color: "white" }}>Check out our</Text>
            <TextLink
              style={{ color: colors.primary300, textAlign: "center" }}
              type="route"
              routeName="DataAgreement"
            >
              data privacy agreement
            </TextLink>
          </View>
        </Pressable>
      )}

      <View style={{ flex: 1 }} />
      {mobileMode && (
        <>
          {footerElements({
            textAlign: "right",
            paddingRight: entryRightPadding,
          })}
          <View style={{ height: UNIT }} />
        </>
      )}
    </View>
  );
}
